import React from 'react';
import './TrackingPreview.css';

/* TrackingPreview — faithful mirror of trackingStatic/tracking_page.js v2
   (2026-10-06). Modes: 'search' (mobile lookup), 'result' (mobile status
   screen), 'desktop' (wide search). Same derivation rules as the widget:
   gradient hero from the brand color, auto black/white contrast text. */

const shadeColor = (hex, factor) => {
  let h = (hex || '#2d2d2d').replace('#', '');
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const f = (i) => Math.max(0, Math.min(255, Math.round(parseInt(h.substr(i, 2), 16) * factor)));
  return `#${[f(0), f(2), f(4)].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
};

const contrastColor = (hex) => {
  let h = (hex || '#2d2d2d').replace('#', '');
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const yiq = (parseInt(h.substr(0, 2), 16) * 299 + parseInt(h.substr(2, 2), 16) * 587 + parseInt(h.substr(4, 2), 16) * 114) / 1000;
  return yiq >= 128 ? '#1F2937' : '#ffffff';
};

const Icon = {
  check: (props) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M20 6L9 17l-5-5" /></svg>
  ),
  checkSm: (props) => (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M20 6L9 17l-5-5" /></svg>
  ),
  truck: (props) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M1 7h12v9H1z" /><path d="M13 10h4l3 3v3h-7" /><circle cx="5.5" cy="17.5" r="1.6" /><circle cx="16.5" cy="17.5" r="1.6" /></svg>
  ),
  copy: (props) => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></svg>
  ),
};

const TrackingPreview = ({ settings, mode = 'search', t }) => {
  const colors = settings.colors || {};
  const banner = settings.banner || {};
  const branding = settings.branding || {};

  const primary = colors.primary_color || '#2d2d2d';
  const deep = shadeColor(primary, 0.55);
  const onPrimary = contrastColor(primary);
  const heroImage = banner.enabled && banner.background_image ? banner.background_image : null;

  const vars = {
    '--tp-primary': primary,
    '--tp-deep': deep,
    '--tp-on-primary': onPrimary,
    '--tp-secondary': colors.secondary_color || '#f8f9fa',
    '--tp-bg': colors.background_color || '#ffffff',
    '--tp-text': colors.text_color || '#333333',
    '--tp-border': colors.border_color || '#e0e0e0',
    '--tp-success': colors.success_color || '#28a745',
  };

  const title = (banner.enabled && banner.title) || t('trackingSettingsV2.previewDefaultTitle', 'Suivez votre colis');
  const subtitle = (banner.enabled && banner.subtitle) || '';

  const hero = (
    <div
      className={`tp-hero${heroImage ? ' has-image' : ''}`}
      style={heroImage ? { backgroundImage: `url(${heroImage})` } : undefined}
    >
      <div className="tp-hero-brand">
        {branding.company_logo
          ? <img className="tp-hero-logo" src={branding.company_logo} alt="" />
          : <span className="tp-hero-logo tp-hero-logo-letter">{(branding.company_name || 'U').charAt(0).toUpperCase()}</span>}
        {branding.company_name && <b>{branding.company_name}</b>}
      </div>
      <h2>{title}</h2>
      {subtitle && <p>{subtitle}</p>}
    </div>
  );

  const searchCard = (
    <div className="tp-card">
      <h3>{t('trackingSettingsV2.previewAccess', 'Accéder à vos commandes')}</h3>
      <p>{t('trackingSettingsV2.previewAccessHint', 'Entrez votre e-mail pour recevoir un code de vérification.')}</p>
      <label>{t('trackingSettingsV2.previewEmail', 'E-mail')}</label>
      <div className="tp-input">client@exemple.fr</div>
      <div className="tp-btn">{t('trackingSettingsV2.previewSendCode', 'Recevoir le code')}</div>
    </div>
  );

  const footer = branding.show_powered_by !== false && (
    <div className="tp-powered">{t('trackingSettingsV2.previewPoweredBy', 'Propulsé par')} <strong>Unanimity AI</strong></div>
  );

  if (mode === 'result') {
    const steps = [
      { label: t('trackingSettingsV2.previewStep1', 'Confirmée'), state: 'done' },
      { label: t('trackingSettingsV2.previewStep2', 'Expédiée'), state: 'done' },
      { label: t('trackingSettingsV2.previewStep3', 'En livraison'), state: 'now' },
      { label: t('trackingSettingsV2.previewStep4', 'Livrée'), state: '' },
    ];
    return (
      <div className="tp-stage tp-mobile" style={vars}>
        {hero}
        <div className="tp-float">
          <div className="tp-card">
            <div className="tp-status">
              <span className="tp-status-ic"><Icon.truck /></span>
              <span>
                <b>{t('trackingSettingsV2.previewStatus', 'En cours de livraison')}</b>
                <small>{t('trackingSettingsV2.previewStatusSub', 'Dernière mise à jour · aujourd’hui')}</small>
              </span>
            </div>
            <div className="tp-chip">
              <span>Colissimo · <b>6A210000000</b></span>
              <span className="tp-chip-copy"><Icon.copy /></span>
            </div>
            <div className="tp-steps">
              {steps.map((s) => (
                <div className={`tp-step ${s.state}`} key={s.label}>
                  <span className="tp-step-dot">{s.state === 'done' && <Icon.checkSm />}</span>
                  <span className="tp-step-label">{s.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="tp-card">
            <h4>{t('trackingSettingsV2.previewTimeline', 'Historique')}</h4>
            <div className="tp-ev"><span className="tp-ev-dot" /><span><b>{t('trackingSettingsV2.previewEv1', 'Colis en cours d’acheminement')}</b><small>09:12 · Paris</small></span></div>
            <div className="tp-ev past"><span className="tp-ev-dot" /><span><b>{t('trackingSettingsV2.previewEv2', 'Colis pris en charge')}</b><small>{t('trackingSettingsV2.previewEv2Sub', 'hier · 17:45')}</small></span></div>
          </div>
        </div>
        {footer}
      </div>
    );
  }

  if (mode === 'desktop') {
    return (
      <div className="tp-stage tp-desktop" style={vars}>
        {hero}
        <div className="tp-float">{searchCard}</div>
        {footer}
      </div>
    );
  }

  return (
    <div className="tp-stage tp-mobile" style={vars}>
      {hero}
      <div className="tp-float">{searchCard}</div>
      {footer}
    </div>
  );
};

export default TrackingPreview;
